/**
 * Dashboard Context
 * Provides dashboard state and actions via Context API
 */

import { createContext, useContext } from 'react';
import type {
  InvoiceDto,
  FiltersDto,
  InvoiceSearchRequestDto,
  InvoiceStatusCategoryStatsDto,
  KeyMetricsResponseDto,
} from 'src/generated/services/TFinancialApi';

export interface DashboardContextValue {
  // Data
  items: InvoiceDto[];
  totalItems: number;
  statusStats: InvoiceStatusCategoryStatsDto;
  dataStats: KeyMetricsResponseDto;

  allowedCurrencies: string[];

  // Filter state
  filters: {
    currencyReference: string;
    dateReference: InvoiceSearchRequestDto['dateReference'];
    invoiceStatusCategory: InvoiceSearchRequestDto['invoiceStatusCategory'];
    invoiceStatusSet: InvoiceSearchRequestDto['invoiceStatusSet'];
    filters: FiltersDto;
  };
  page: number;
  pageSize: number;
  orderBy: string;
  orderMethod: 'asc' | 'desc';

  // Prerequisites
  currencies: string[];

  // Filter handlers
  onFilterChange: (
    filters: Partial<{
      currencyReference: string;
      dateReference: InvoiceSearchRequestDto['dateReference'];
      invoiceStatusCategory: InvoiceSearchRequestDto['invoiceStatusCategory'];
      invoiceStatusSet: InvoiceSearchRequestDto['invoiceStatusSet'];
      filters: FiltersDto;
    }>
  ) => void;
  onTableFilter: (columnId: string, values: string[]) => void;
  onStatusChange: (status: InvoiceSearchRequestDto['invoiceStatusCategory']) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSort: (method: 'asc' | 'desc', field: string) => void;
  onReset: () => void;
  onRefresh: () => void;

  // Helper functions
  getColumnCounterInfo: (columnId: string, allOptionsCount: number) => { showCounter: boolean; counterValue: number };

  // Loading states - Granular for progressive loading
  isLoadingInvoiceData: boolean; // Phase 1: Basic invoice data (show table after this)
  isLoadingChargeData: boolean; // Phase 2: Charge/deviation data (merge after this)
  isLoadingInvoiceStats: boolean; // Phase 1: Basic metrics (show metrics after this)
  isLoadingChargeStats: boolean; // Phase 2: Charge metrics (merge after this)
  isLoadingMetadata: boolean;
}

export const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

/**
 * Hook to consume Dashboard context
 * Must be used within DashboardProvider
 */
export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }

  return context;
};
